import React, { useState } from 'react';
import { Button, Disclaimer, Popup, QrCode } from '@repo/design-system-kit';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSegment } from '@repo/poly-hooks/segment';
import { faqService } from '../../../utils/faq-composer';
import styles from './sca.module.css';

interface EnforceQRCodeProps {
  onClose?: () => void;
  onSms: () => void;
  qrCodeUrl: string;
}

export function EnforceQRCode({ onClose, onSms, qrCodeUrl }: EnforceQRCodeProps): React.ReactNode {
  // NOTE -- this is needed until we have a better way to handle the portal container in qunit tests
  // https://www.notion.so/qonto/React-popover-library-used-within-react-aria-components-does-mount-popovers-modals-outside-of-qunit--12631ee4c696806e87dbef81e9a27922
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  const segment = useSegment();
  const [isOpen, setIsOpen] = useState(true);
  const [smsStatus, setSmsStatus] = useState<'not-sent' | 'sending' | 'sent'>('not-sent');
  const { formatMessage } = useIntl();

  const handleClose = (): void => {
    segment.track('sca_enforcement_closed', { origin: 'modal dismissed' });
    onClose?.();
    setIsOpen(false);
  };

  const handleSms = (): void => {
    setSmsStatus('sending');
    onSms();
    setSmsStatus('sent');
  };

  return (
    <Popup.Root
      aria-label={formatMessage({ id: 'sca.enforcement.title' })}
      data-testid="sca-enforcement-popup"
      isOpen={isOpen}
      portalContainer={TEST_PORTAL_CONTAINER}
      role="dialog"
    >
      <Popup.CloseButton onPress={handleClose} />
      <Popup.Header>
        <Popup.Title>
          <FormattedMessage id="sca.enforcement.title" />
        </Popup.Title>
        <Popup.Subtitle>
          <FormattedMessage
            id="sca.enforcement.subtitle"
            values={{
              a: (chunks: React.ReactNode) => (
                <a
                  className={styles.faqLink}
                  href={faqService.getLocalizedArticle('how_to_pair_my_phone')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </Popup.Subtitle>
      </Popup.Header>
      <Popup.Body>
        <main className={styles.container}>
          <article className={styles.insetBox}>
            <QrCode url={qrCodeUrl} />
            <FormattedMessage id="sca.enforcement.separator">
              {text => <span className={styles.separator}>{text}</span>}
            </FormattedMessage>
            <Button
              isDisabled={smsStatus === 'sending'}
              onPress={handleSms}
              stretch
              variant="secondary"
            >
              {smsStatus === 'sent' ? (
                <FormattedMessage id="sca.enforcement.resend-sms" />
              ) : (
                <FormattedMessage id="sca.enforcement.sms" />
              )}
            </Button>
          </article>
          <Disclaimer.Block block level="info">
            <FormattedMessage
              id="sca.enforcement.disclaimer.text"
              values={{
                a: (chunks: React.ReactNode) => (
                  <a
                    href={faqService.getLocalizedArticle('what_is_sca')}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Disclaimer.Block>
        </main>
      </Popup.Body>
    </Popup.Root>
  );
}
